import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BaseModal from '../../utils/BaseModal';
import { OfferDTO } from '../../user/on-sale/types';
import ItemAnalytics from './analytics/ItemAnalytics';
import BuyOrderList from './buy-order/BuyOrderList';
import ItemDetails from './item/ItemDetails';
import SellerDetails from './item/SellerDetails';
import CheckOut from './item/CheckOut';
import { RootState } from '../../../store';

type Props = {
  open: boolean;
  onClose: () => void;
  offer: OfferDTO | null;
  onAddToCart: (offer: OfferDTO) => void;
  onRemoveFromCart: (offerId: string) => void;
  onBuyNow: (offer: OfferDTO) => void;
  cartLoading?: boolean;
  buyLoading?: boolean;
};

const OfferDetailModal: React.FC<Props> = ({
                                             open,
                                             onClose,
                                             offer: initialOffer,
                                             onAddToCart,
                                             onRemoveFromCart,
                                             onBuyNow,
                                             cartLoading = false,
                                             buyLoading = false
                                           }) => {
  const cartItems = useSelector((state: RootState) => state.cart.items);

  // Local state to handle immediate status updates (e.g. BOUGHT)
  const [localOffer, setLocalOffer] = useState<OfferDTO | null>(initialOffer);
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    setLocalOffer(initialOffer);
  }, [initialOffer]);

  // Sync cart status
  useEffect(() => {
    if (localOffer) {
      const inCart = cartItems.some((item) => item.offerId === localOffer.id);
      setIsAddedToCart(inCart);
    }
  }, [cartItems, localOffer]);

  // Listen for global buy events
  useEffect(() => {
    const handleGlobalBuy = (event: any) => {
      const boughtIds = event.detail?.offerIds || [];
      if (localOffer && boughtIds.includes(localOffer.id)) {
        setLocalOffer(prev => prev ? { ...prev, status: 'BOUGHT' } : null);
      }
    };

    window.addEventListener('offers-bought', handleGlobalBuy);
    return () => window.removeEventListener('offers-bought', handleGlobalBuy);
  }, [localOffer?.id]);

  if (!localOffer) return null;

  const isBought = localOffer.status === 'BOUGHT';

  return (
      <BaseModal open={open} onAbort={onClose} maxWidth="lg">
        <div className="flex flex-col gap-6 p-1">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT: Item Image & Rarity (5/12) */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <ItemDetails offer={localOffer}/>
            </div>

            {/* MIDDLE: Buy Orders / Market History (3/12) */}
            <div className="lg:col-span-3 order-2 lg:order-1 max-h-[400px] lg:h-[400px]">
              <BuyOrderList
                  marketHashName={localOffer.item.catalog?.marketHashName ?? undefined}
                  referencePriceMinor={localOffer.item.catalog.priceReal24hMinor ?? undefined}
              />
            </div>

            {/* RIGHT: Seller Info & Checkout Actions (4/12) */}
            <div className="lg:col-span-4 order-3 flex flex-col gap-3 lg:h-[400px]">
              <SellerDetails offer={localOffer}/>
              <CheckOut
                  offer={localOffer}
                  onAddToCart={() => onAddToCart(localOffer)}
                  onRemoveFromCart={() => onRemoveFromCart(localOffer.id)}
                  onBuyNow={() => onBuyNow(localOffer)}
                  isAddedToCart={isAddedToCart}
                  isBought={isBought}
                  cartLoading={cartLoading}
                  buyLoading={buyLoading}
              />
            </div>
          </div>

          {/* BOTTOM: Pricing Charts & Analytics */}
          <div className="order-4">
            <ItemAnalytics
                itemAssetId={localOffer.item.itemAssetId}
                wearName={localOffer.item.catalog?.wearName ?? undefined}
            />
          </div>
        </div>
      </BaseModal>
  );
};

export default OfferDetailModal;